import { useEffect, useRef, useState } from "react";

/**
 * useCountUp — rAF-driven counter-animation für integer-targets (z.B. hitze-stufen 1..4).
 *
 * Spring-approximation der CSS-easing `cubic-bezier(0.34, 1.56, 0.64, 1)` —
 * minimaler overshoot, der bei integer-targets via `Math.round` neutralisiert wird
 * (visuell snappt auf target, formel-overshoot bleibt unter 1).
 *
 * Respektiert `prefers-reduced-motion: reduce` (rAF wird vom globalen
 * css-media-query nicht gestoppt — explizit hier abgefangen).
 *
 * One-shot per `resetKey` — animation läuft NUR bei resetKey-change, nicht bei
 * react-query-refetch im 5min-staletime-intervall. Default-resetKey=undefined
 * → läuft genau einmal beim mount.
 */
interface UseCountUpOptions {
  target: number;
  duration?: number;
  resetKey?: string | number;
}

function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

export function useCountUp({ target, duration = 700, resetKey }: UseCountUpOptions): number {
  const [value, setValue] = useState(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReducedMotion) {
      setValue(target);
      return;
    }

    const start = performance.now();
    const from = 1;

    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = easeOutBack(t);
      setValue(Math.round(from + (target - from) * eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, resetKey]);

  return value;
}
